import React, { useContext } from 'react'

import { useCachedData } from 'hooks/useCachedData'
import { usePollApi } from 'hooks/usePollApi'
import { Loadable, catLoadables, mapLoadable, mkLoadable } from 'utils/loadable'

export type Exchange = {
  id: number
  name: string
}

type ExchangeCollections = Loadable<{
  exchanges: Exchange[]
  exchangesById: { [k: number]: Exchange }
  exchangesByName: { [k: string]: Exchange }
}>

export type ExchangeApi = {
  api_id: number
  name: string
}

export type CredentialProvider = {
  id: number
  name: string
}

const ExchangesContext = React.createContext<ExchangeCollections>({
  loading: true,
})
const ExchangeApisContext = React.createContext<Loadable<ExchangeApi[]>>({
  loading: true,
})
const CredentialProvidersContext = React.createContext<
  Loadable<CredentialProvider[]>
>({ loading: true })

type Children = { children: React.ReactNode }

export function ExchangesContextProvider(props: Children): JSX.Element {
  const exchanges = useCachedData(
    'contexts:Exchanges',
    mkLoadable(
      usePollApi<Exchange>(
        { path: 'exchanges', params: new URLSearchParams({ order: 'name' }) },
        60000,
      ),
    ),
  )

  const exchangesById = mapLoadable(exchanges, data =>
    Object.fromEntries(data.map(e => [e.id, e])),
  )

  const exchangesByName = mapLoadable(exchanges, data =>
    Object.fromEntries(data.map(e => [e.name, e])),
  )

  const value = catLoadables({
    exchanges,
    exchangesById,
    exchangesByName,
  })

  return (
    <ExchangesContext.Provider value={value}>
      {props.children}
    </ExchangesContext.Provider>
  )
}

export function ExchangeApisContextProvider(props: Children): JSX.Element {
  const value = useCachedData(
    'contexts:ExchangeApis',
    mkLoadable(usePollApi<ExchangeApi>('apis', 60000)),
  )

  return (
    <ExchangeApisContext.Provider value={value}>
      {props.children}
    </ExchangeApisContext.Provider>
  )
}

export function CredentialProvidersContextProvider(
  props: Children,
): JSX.Element {
  const value = useCachedData(
    'contexts:CredentialProviders',
    mkLoadable(usePollApi<CredentialProvider>('credential_providers', 60000)),
  )

  return (
    <CredentialProvidersContext.Provider value={value}>
      {props.children}
    </CredentialProvidersContext.Provider>
  )
}

export const ExchangesProvider = (props: Children): JSX.Element => (
  <ExchangesContextProvider>
    <ExchangeApisContextProvider>
      <CredentialProvidersContextProvider>
        {props.children}
      </CredentialProvidersContextProvider>
    </ExchangeApisContextProvider>
  </ExchangesContextProvider>
)

export const useExchanges = (): Loadable<Exchange[]> =>
  mapLoadable(useContext(ExchangesContext), data => data.exchanges)

export const useExchangeApis = (): Loadable<ExchangeApi[]> =>
  useContext(ExchangeApisContext)

export const useCredentialProviders = (): Loadable<CredentialProvider[]> =>
  useContext(CredentialProvidersContext)

export const useExchange = (id: number): Loadable<Exchange | undefined> =>
  mapLoadable(useContext(ExchangesContext), data => data.exchangesById[id])

export const useExchangeByName = (
  name: string,
): Loadable<Exchange | undefined> =>
  mapLoadable(useContext(ExchangesContext), data => data.exchangesByName[name])
